// View-local purchase-flow controller for the pricing table. Owns selection
// state, selection analytics, checkout orchestration, and CTA state derived
// from the shared product/payment/checkout hooks.

import { useCallback, useMemo, useState } from 'react';
import { useProductCheckout } from './useProductCheckout';
import { useProductPaymentMethods } from './useProductPaymentMethods';
import { analyticsService } from '../services/analyticsService';
import type { AnalyticsSurface } from '../types/analytics';
import type { ProductDocument } from '@airhead-games/shared/types';
import {
  getBaselinePricePerLoon,
  getDefaultLoonProduct,
} from '@airhead-games/shared/utilities';

type CheckoutButtonLabel = 'Success!' | 'Buy' | 'Checkout';

interface UseProductPricingTableOptions {
  products: ProductDocument[];
  analyticsSurface: AnalyticsSurface;
  /** Called when a saved-method quick purchase succeeds (e.g. to close a dialog). */
  onPurchaseSuccess?: () => void;
}

export interface UseProductPricingTableResult {
  activePricingPriceId: string | null;
  baselinePricePerLoon: number | null;
  onSelectPricingPack: (stripePriceId: string) => void;
  onCheckout: () => Promise<void>;
  isCheckoutLoading: boolean;
  isCheckoutSuccess: boolean;
  isCheckoutEnabled: boolean;
  checkoutButtonLabel: CheckoutButtonLabel;
  checkoutError: string | null;
}

export function useProductPricingTable({
  products,
  analyticsSurface,
  onPurchaseSuccess,
}: UseProductPricingTableOptions): UseProductPricingTableResult {
  const { paymentMethods, hasSavedMethod } = useProductPaymentMethods();
  const {
    checkoutProduct,
    activeCheckoutPriceId,
    purchaseSuccessPriceId,
    checkoutError,
  } = useProductCheckout();
  const [selectedPricingPriceId, setSelectedPricingPriceId] = useState<
    string | null
  >(null);

  const defaultSelectedProduct = useMemo(
    () => getDefaultLoonProduct(products),
    [products]
  );

  const activePricingPriceId = useMemo(() => {
    if (
      selectedPricingPriceId &&
      products.some(
        (product) => product.stripePriceId === selectedPricingPriceId
      )
    ) {
      return selectedPricingPriceId;
    }
    return defaultSelectedProduct?.stripePriceId || null;
  }, [defaultSelectedProduct, products, selectedPricingPriceId]);

  const selectedPricingProduct = useMemo(() => {
    if (!activePricingPriceId) {
      return null;
    }
    return (
      products.find(
        (product) => product.stripePriceId === activePricingPriceId
      ) || null
    );
  }, [activePricingPriceId, products]);

  const baselinePricePerLoon = useMemo(
    () => getBaselinePricePerLoon(products),
    [products]
  );

  const onSelectPricingPack = useCallback(
    (stripePriceId: string) => {
      setSelectedPricingPriceId((current) => {
        if (current === stripePriceId) {
          return current;
        }
        const productSelected =
          products.find((product) => product.stripePriceId === stripePriceId) ||
          null;
        analyticsService.trackLoonPackSelected({
          surface: analyticsSurface,
          stripe_price_id: stripePriceId,
          product_name: productSelected?.name,
          loons_granted: productSelected?.loonsGranted ?? undefined,
          list_price: productSelected?.listPrice,
          currency: productSelected?.currency,
          has_saved_method: hasSavedMethod,
        });
        return stripePriceId;
      });
    },
    [analyticsSurface, hasSavedMethod, products]
  );

  const onCheckout = useCallback(async () => {
    if (!selectedPricingProduct) {
      return;
    }
    await checkoutProduct({
      product: selectedPricingProduct,
      trackingLabel: hasSavedMethod ? 'Buy' : 'Checkout',
      trackingContext: {
        surface: analyticsSurface,
        flow_type: hasSavedMethod ? 'quick_purchase' : 'stripe_checkout',
      },
      savedPaymentMethodId: paymentMethods[0]?.id ?? null,
      onQuickPurchaseSuccess: onPurchaseSuccess,
    });
  }, [
    analyticsSurface,
    checkoutProduct,
    hasSavedMethod,
    onPurchaseSuccess,
    paymentMethods,
    selectedPricingProduct,
  ]);

  const isCheckoutLoading =
    activeCheckoutPriceId === selectedPricingProduct?.stripePriceId;
  const isCheckoutSuccess =
    purchaseSuccessPriceId === selectedPricingProduct?.stripePriceId;
  const isCheckoutEnabled = !!selectedPricingProduct && !isCheckoutLoading;

  const checkoutButtonLabel: CheckoutButtonLabel = isCheckoutSuccess
    ? 'Success!'
    : hasSavedMethod
      ? 'Buy'
      : 'Checkout';

  return {
    activePricingPriceId,
    baselinePricePerLoon,
    onSelectPricingPack,
    onCheckout,
    isCheckoutLoading,
    isCheckoutSuccess,
    isCheckoutEnabled,
    checkoutButtonLabel,
    checkoutError,
  };
}
